import * as React from 'react';
import { Theme, alpha } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useSupdateSpotServiceMutation, Spot, UpdateSpotServiceInput } from '@graphql';
import { useToastError } from '@hooks';
import { TriStateCheckbox } from '../common';

interface RenderSwitchProps {
  service: keyof UpdateSpotServiceInput;
}
interface SpotServiceProps {
  account: Spot;
}

export function SpotService(props: SpotServiceProps) {
  const { account } = props;
  const [timeoutId, setTimeoutId] = React.useState<NodeJS.Timeout | null>(null);
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const [updateSpotTypeMutation] = useSupdateSpotServiceMutation();

  const initialValues = React.useMemo(() => {
    return Object.entries(account.service)
      .filter(([key, value]) => !['__typename', 'id', 'updatedAt'].includes(key))
      .reduce((acc, [key, value]) => {
        acc[key as keyof UpdateSpotServiceInput] = value;
        return acc;
      }, {} as Record<keyof UpdateSpotServiceInput, boolean | null>);
  }, [account.service]);

  const validationSchema = React.useMemo(() => {
    return yup.object(
      Object.keys(account.service)
        .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
        .reduce((acc, key) => {
          acc[key as keyof UpdateSpotServiceInput] = yup.boolean().nullable().defined();
          return acc;
        }, {} as Record<keyof UpdateSpotServiceInput, yup.BooleanSchema<boolean | null>>)
    );
  }, [account.service]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (formik.dirty) {
        updateSpotTypeMutation({
          variables: { id: account.id, input: values },
          onCompleted: ({ supdateSpotService }) => {
            setTimeoutId(null);
            formik.resetForm({ values });
            toast(t('spotService.toast.success', { ns: 'field' }), { type: 'success' });
          },
          onError: toastError,
        });
      }
    },
  });

  React.useEffect(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    if (formik.dirty && formik.isValid) {
      const newTmoutId = setTimeout(formik.submitForm, 1000);
      setTimeoutId(newTmoutId);
    }
  }, [formik.dirty, formik.isValid, formik.values]);

  if (account?.__typename !== 'Spot') {
    return null;
  }

  return (
    <Stack width="100%" spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          p: 3,
          borderRadius: (theme: Theme) => theme.borderRadius,
          backgroundColor: alpha('#fff', 0.1),
        }}
      >
        <Stack>
          <Typography fontWeight="bold">{t('spotService.label', { ns: 'field' })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('spotService.caption', { ns: 'field' })}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        divider={<Divider sx={{ backgroundColor: (theme: Theme) => theme.palette.grey[800] }} />}
      >
        {Object.keys(account.service)
          .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
          .map((key) => (
            <TriStateCheckbox
              key={key}
              label={t(`service.${key}`, { ns: 'spot' })}
              dataKey={key}
              formik={formik}
            />
          ))}
      </Stack>
    </Stack>
  );
}
